import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Shared "draw-in when scrolled into view" state for `BarChart`/`DonutChart`/`LineChart` (and
 * `Panel`'s own entrance) — a chart/card that reveals itself the instant it mounts reads as static
 * and inert for anything below the fold, because React mounts the whole page at once: by the time
 * a viewer actually scrolls to it, a mount-triggered animation finished fanning out seconds ago,
 * off-screen, unseen. Returns a `ref` to attach to the element's own root (the thing whose
 * visibility actually matters — the SVG/card, not some ancestor) and a `revealed` boolean that
 * flips `true` the first time that element scrolls into the viewport, via `IntersectionObserver`.
 * `once: true` semantics — the observer disconnects after the first reveal, so this is a one-time
 * entrance, not a replay-every-scroll scrubber.
 *
 * `rootMargin: '0px 0px -40px 0px'` shrinks the trigger zone by 40px from the bottom of the
 * viewport, so a card whose top sliver just barely pokes into view (not yet meaningfully visible
 * to a real reader) doesn't fire early.
 *
 * Falls back to revealing immediately when `IntersectionObserver` doesn't exist (older browsers,
 * and — importantly — this project's jsdom-based component tests, which don't polyfill it) rather
 * than throwing or leaving content permanently hidden.
 */
export function useDrawIn<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, revealed];
}
